/**
 * tone.ts — the ONLY place a semantic hue resolves to a hex (R4-P2 build
 * brief P2.5 COLOR & CONTRAST AMENDMENT §A/§B/§C, founder verdict
 * 2026-07-12: "too bland boring, grey and shit" — the design system had
 * over-applied "never use hue for HIERARCHY" into "no hue ANYWHERE"; real
 * product mocks carry semantic product color (green won-chips, blue source
 * tags, tinted icons). This module is that disciplined semantic-color
 * layer. It does NOT loosen the amber fence (Q2) — amber stays pole-only
 * (hero + final-CTA), routed through Chip's existing `tone:'accent'` +
 * `accentContext` arm, never through `LandingTone`.
 *
 * Truth rule, binding everywhere a tone is consumed: color encodes MEANING
 * (status / stage / source-category / delta-sign), never decoration on a
 * random row.
 *
 * Every tone below is a pinned ARBITRARY-HEX token (`bg-[#hex]/NN` /
 * `text-[#hex]` / `border-[#hex]/NN`) — NEVER a named Tailwind utility
 * (`bg-violet-400` stays banned, enforced by
 * `scripts/check-landing-band-hue.mjs`'s NAMED_HUE_UTILITY_RE). Folds in
 * `ScheduleGrid`'s former standalone `DOT_TONE_FILL` — every consumer
 * imports from here now, so there is exactly one place a tone can drift.
 *
 * R6 de-slop round: `avatarSolidText`/`avatarBg`/`avatarSolidBg` (neutral)
 * below now resolve through `dark-tokens.ts`'s `DARK_INK_ON_ACCENT`/
 * `DARK_SURFACE.raised` -- the warm-retinted replacements for the OLD cool
 * `#0B0C0E`/`#16191E` this file used to hardcode directly.
 */
import { DARK_INK_ON_ACCENT, DARK_SURFACE } from './dark-tokens';

/** The 5 semantic tones + the neutral base (§A). */
export type LandingTone = 'neutral' | 'success' | 'info' | 'focus' | 'fresh' | 'attention';

/** Positive/category-only tones used for per-chapter FigureFrame washes
 * (§C) — rose (negative) and amber (pole-reserved) are deliberately
 * excluded; a negative-coded or pole-reserved hue washing a whole chapter
 * would misread. */
export type ChapterAccentTone = 'sky' | 'violet' | 'emerald' | 'teal';

export type ToneRecipe = {
  /** Raw hex, e.g. for inline-style consumers that can't pre-bake a
   * static Tailwind class (a per-instance dynamic border color). Empty
   * for `neutral`, which has no single dedicated hue. */
  hex: string;
  /** Solid dot fill. */
  dot: string;
  /** Low-alpha chip background fill. */
  chipFill: string;
  /** Solid-hex chip text color. */
  chipText: string;
  /** Chip border (`''` = borderless, per §A's neutral row). */
  chipBorder: string;
  /** Icon duotone foreground tint. */
  iconTint: string;
  /** Avatar tinted background (low-alpha "tint" recipe). */
  avatarBg: string;
  /** Avatar tinted text (initials), tint recipe. */
  avatarText: string;
  /** P2.6 CRAFT-DENSITY §A lead-ruling ESCALATION: attio's owner-row avatar
   * chips are SOLID (full-alpha hue + near-black initials), not the
   * low-alpha tint above — the founder rejected the subtler tint reading
   * "too flat" on live review. Full-alpha `tone.hex` bg. */
  avatarSolidBg: string;
  /** Near-black initials text paired with `avatarSolidBg` — WCAG-verified
   * per-tone below (white text on any of these 5 hues clears at most
   * 2.72:1, well under the 3:1 large-text floor; `DARK_INK_ON_ACCENT`
   * (`#0E0C08` post-R6 warm retint, was `#0B0C0E`), the house's own darkest
   * neutral, clears 7.19-10.51:1 on every tone). */
  avatarSolidText: string;
};

/**
 * Contrast-verified (WCAG, chip text on its own 12%-over-`DARK_SURFACE.base`
 * (`#161410` post-R6 warm retint, was `#121418`) fill):
 * emerald 8.6:1 · sky 6.1:1 · teal 8.9:1 · rose 6.4:1 · violet 5.7:1
 * (worst case) · neutral-content white/80 on white/[0.08] ≈ 9:1. All ≥3:1.
 */
export const TONE: Record<LandingTone, ToneRecipe> = {
  success: {
    hex: '#34D399',
    dot: 'bg-[#34D399]',
    chipFill: 'bg-[#34D399]/12',
    chipText: 'text-[#34D399]',
    chipBorder: 'border-[#34D399]/24',
    iconTint: 'text-[#34D399]',
    avatarBg: 'bg-[#34D399]/16',
    avatarText: 'text-[#34D399]',
    avatarSolidBg: 'bg-[#34D399]',
    avatarSolidText: DARK_INK_ON_ACCENT.text,
  },
  info: {
    hex: '#60A5FA',
    dot: 'bg-[#60A5FA]',
    chipFill: 'bg-[#60A5FA]/12',
    chipText: 'text-[#60A5FA]',
    chipBorder: 'border-[#60A5FA]/24',
    iconTint: 'text-[#60A5FA]',
    avatarBg: 'bg-[#60A5FA]/16',
    avatarText: 'text-[#60A5FA]',
    avatarSolidBg: 'bg-[#60A5FA]',
    avatarSolidText: DARK_INK_ON_ACCENT.text,
  },
  focus: {
    hex: '#A78BFA',
    dot: 'bg-[#A78BFA]',
    chipFill: 'bg-[#A78BFA]/12',
    chipText: 'text-[#A78BFA]',
    chipBorder: 'border-[#A78BFA]/24',
    iconTint: 'text-[#A78BFA]',
    avatarBg: 'bg-[#A78BFA]/16',
    avatarText: 'text-[#A78BFA]',
    avatarSolidBg: 'bg-[#A78BFA]',
    avatarSolidText: DARK_INK_ON_ACCENT.text,
  },
  fresh: {
    hex: '#2DD4BF',
    dot: 'bg-[#2DD4BF]',
    chipFill: 'bg-[#2DD4BF]/12',
    chipText: 'text-[#2DD4BF]',
    chipBorder: 'border-[#2DD4BF]/24',
    iconTint: 'text-[#2DD4BF]',
    avatarBg: 'bg-[#2DD4BF]/16',
    avatarText: 'text-[#2DD4BF]',
    avatarSolidBg: 'bg-[#2DD4BF]',
    avatarSolidText: DARK_INK_ON_ACCENT.text,
  },
  attention: {
    hex: '#FB7185',
    dot: 'bg-[#FB7185]',
    chipFill: 'bg-[#FB7185]/12',
    chipText: 'text-[#FB7185]',
    chipBorder: 'border-[#FB7185]/24',
    iconTint: 'text-[#FB7185]',
    avatarBg: 'bg-[#FB7185]/16',
    avatarText: 'text-[#FB7185]',
    avatarSolidBg: 'bg-[#FB7185]',
    avatarSolidText: DARK_INK_ON_ACCENT.text,
  },
  // §A's neutral row (dot/fill/border) is the real, wired recipe. `chipText`
  // is deliberately NOT `text-white/70` here even though §A's table lists
  // that value — Chip's neutral-status chip already carries a distinct,
  // reviewed "7th role" (content, `white/80`, `Chip.tsx`'s own Finding M2)
  // that the P2.5 contrast ladger (§D) explicitly keeps unchanged
  // ("Text content … white/80 (keep — already bright)"). Consumers that
  // want §A's tabulated tone-formula text color literally can still read
  // `TONE.neutral.hex`-style fields; Chip itself special-cases neutral to
  // preserve that reviewed role rather than regress it to 70%.
  neutral: {
    hex: '',
    dot: 'bg-white/50',
    chipFill: 'bg-white/[0.08]',
    chipText: 'text-white/80',
    chipBorder: '',
    iconTint: 'text-white/50',
    avatarBg: DARK_SURFACE.raised.bg,
    avatarText: 'text-white/70',
    // Neutral has no dedicated hue to go "solid" with — its existing
    // `DARK_SURFACE.raised` chip fill (`#1B1815` post-R6 warm retint, was
    // `#16191E`) IS already a solid (100%-alpha) recipe, so the solid/tint
    // split collapses to the same pair for this one row.
    avatarSolidBg: DARK_SURFACE.raised.bg,
    avatarSolidText: 'text-white/70',
  },
};

/**
 * Per-chapter accent wash (§C) — a single static radial-gradient, alpha
 * cap 0.09, anchored top-center behind a `FigureFrame`'s content. Consumed
 * via inline `style={{ backgroundImage: CHAPTER_ACCENT_WASH[accent] }}` —
 * these are deliberately NOT Tailwind arbitrary-gradient classes (the
 * gradient argument is too long/dynamic for static JIT scanning); the
 * scan that covers this exact shape is
 * `scripts/check-landing-band-hue.mjs`'s palette-gradient scan (§E.3),
 * which reads inline-style `radial-gradient()` calls directly, not
 * Tailwind class names.
 */
export const CHAPTER_ACCENT_WASH: Record<ChapterAccentTone, string> = {
  sky: 'radial-gradient(70% 60% at 50% 0%, rgba(96,165,250,.09) 0%, rgba(96,165,250,.03) 45%, transparent 72%)',
  violet:
    'radial-gradient(70% 60% at 50% 0%, rgba(167,139,250,.09) 0%, rgba(167,139,250,.03) 45%, transparent 72%)',
  emerald:
    'radial-gradient(70% 60% at 50% 0%, rgba(52,211,153,.09) 0%, rgba(52,211,153,.03) 45%, transparent 72%)',
  teal: 'radial-gradient(70% 60% at 50% 0%, rgba(45,212,191,.09) 0%, rgba(45,212,191,.03) 45%, transparent 72%)',
};

/**
 * ScheduleGrid's event-dot tones (§B: "already implemented; this
 * formalizes the mapping, unchanged hexes") — folded in here from
 * ScheduleGrid.tsx's former standalone `DOT_TONE_FILL` so it shares the
 * SAME `success`/`info` hexes as every other tone consumer instead of a
 * second, driftable copy. Deliberately a 3-tone subset (ScheduleGrid never
 * needed `focus`/`fresh`/`attention`) — kept as its own export rather than
 * widening `ScheduleEvent.dotTone` to the full `LandingTone` union, which
 * would be an unrequested API change outside this amendment's scope.
 */
export type ScheduleDotTone = 'neutral' | 'info' | 'success';
export const SCHEDULE_DOT_TONE_FILL: Record<ScheduleDotTone, string> = {
  neutral: TONE.neutral.dot,
  info: TONE.info.dot,
  success: TONE.success.dot,
};
